import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Workflow, Plus, Trash2, Loader2, Send, AlertTriangle, Paperclip } from "lucide-react";
import { API } from "@/lib/constants";

const STOP_LABEL = {
  replied: "replied", hard_bounce: "hard bounce", unsubscribed: "unsubscribed",
  spam_reported: "spam report", removed: "removed", do_not_contact: "do-not-contact",
};

export function SequencePanel({ campaign }) {
  const attachments = campaign?.attachments || [];
  const [enabled, setEnabled] = useState(false);
  const [steps, setSteps] = useState([{ name: "Initial message", subject: "", html: "", delay_days: 0, attachment_ids: [] }]);
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testStep, setTestStep] = useState(1);
  const [testEmail, setTestEmail] = useState("");

  const req = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${API}${path}`, { credentials: "include", headers: { "Content-Type": "application/json" }, ...opts });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      const det = d.detail;
      const msg = Array.isArray(det) ? (det[0]?.msg || "Validation error") : (det || `Request failed (${r.status})`);
      throw new Error(msg);
    }
    return d;
  }, []);

  const load = useCallback(async () => {
    if (!campaign?.campaign_id) return;
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/sequence`);
      setView(d);
      setEnabled(d.sequence?.enabled || false);
      if (d.sequence?.steps?.length) {
        setSteps(d.sequence.steps.map((s) => ({
          name: s.name || "", subject: s.subject || "", html: s.html || "",
          delay_days: s.delay_days ?? 3, attachment_ids: s.attachment_ids || [],
        })));
      }
    } catch { /* no sequence yet */ }
  }, [campaign, req]);
  useEffect(() => { load(); }, [load]);

  const setStep = (i, k, v) => setSteps((ss) => ss.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const addStep = () => setSteps((ss) => [...ss, { name: `Follow-up ${ss.length}`, subject: "", html: "", delay_days: 3, attachment_ids: [] }]);
  const removeStep = (i) => setSteps((ss) => ss.filter((_, idx) => idx !== i));
  const toggleAtt = (i, fid) => setStep(i, "attachment_ids",
    steps[i].attachment_ids.includes(fid) ? steps[i].attachment_ids.filter((x) => x !== fid) : [...steps[i].attachment_ids, fid]);

  const save = async () => {
    for (const [i, s] of steps.entries()) {
      if (!s.subject.trim() || !s.html.trim()) return toast.error(`Step ${i + 1}: subject and body are required`);
    }
    setBusy(true);
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/sequence`, {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          steps: steps.map((s) => ({ ...s, delay_days: Number(s.delay_days) || 0 })),
        }),
      });
      toast.success(`Sequence saved (${d.sequence.steps.length} steps)`);
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const testSend = async () => {
    if (!testEmail.includes("@")) return toast.error("Enter a test email");
    setBusy(true);
    try {
      const d = await req(`/campaigns/bespoke/${campaign.campaign_id}/sequence/test-send`, {
        method: "POST", body: JSON.stringify({ step: Number(testStep), to_email: testEmail }),
      });
      toast.success(`Test step ${testStep} sent with ${d.attachments.length} attachment(s). ${d.note}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const mon = view?.reply_monitoring;

  return (
    <div className="border rounded-lg p-4 space-y-4" data-testid="sequence-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-800 font-medium">
          <Workflow className="w-4 h-4 text-violet-600" /> Automated follow-up sequence
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-slate-500">Enabled</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="sequence-enabled" />
        </div>
      </div>

      {mon && mon.mode === "manual_only" && (
        <div className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 flex gap-2" data-testid="sequence-monitoring-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" /> No mailbox connected — replies are tracked manually. Mark replies so follow-ups stop correctly.
        </div>
      )}
      {mon && mon.healthy === false && (
        <div className="text-xs rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-3 py-2 flex gap-2" data-testid="sequence-monitoring-unhealthy">
          <AlertTriangle className="w-4 h-4 shrink-0" /> {mon.reason} Follow-ups are paused until reply monitoring recovers.
        </div>
      )}

      {steps.map((s, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`sequence-step-${i + 1}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-slate-700">
              {i === 0 ? "Step 1 · Initial message" : `Step ${i + 1} · Follow-up`}
            </div>
            {i > 0 && (
              <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => removeStep(i)} data-testid={`sequence-remove-step-${i + 1}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <Input placeholder="Subject" value={s.subject} onChange={(e) => setStep(i, "subject", e.target.value)} data-testid={`sequence-subject-${i + 1}`} />
          <Textarea placeholder="Body (HTML). Use {{greeting}} to place the salutation." value={s.html} onChange={(e) => setStep(i, "html", e.target.value)} data-testid={`sequence-body-${i + 1}`} />
          {i > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Label className="text-xs">Send</Label>
              <Input type="number" min="0" step="0.5" className="w-20" value={s.delay_days} onChange={(e) => setStep(i, "delay_days", e.target.value)} data-testid={`sequence-delay-${i + 1}`} />
              <span className="text-xs text-slate-500">days after this recipient's previous successful send</span>
            </div>
          )}
          {attachments.length > 0 && (
            <div className="space-y-1">
              <div className="text-xs text-slate-500 flex items-center gap-1"><Paperclip className="w-3 h-3" /> Attachments for this step</div>
              <div className="flex flex-wrap gap-3">
                {attachments.map((a) => (
                  <label key={a.file_id} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <Checkbox checked={s.attachment_ids.includes(a.file_id)} onCheckedChange={() => toggleAtt(i, a.file_id)} data-testid={`sequence-att-${i + 1}-${a.file_id}`} />
                    {a.filename}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={addStep} data-testid="sequence-add-step"><Plus className="w-3.5 h-3.5 mr-1" /> Add follow-up</Button>
        <Button size="sm" onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="sequence-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save sequence
        </Button>
      </div>

      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
        <div className="text-xs font-semibold text-slate-600">Test send (verify rendering + attachments)</div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="number" min="1" className="w-16" value={testStep} onChange={(e) => setTestStep(e.target.value)} data-testid="sequence-test-step" />
          <Input placeholder="you@example.com" className="w-56" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="sequence-test-email" />
          <Button size="sm" variant="outline" onClick={testSend} disabled={busy} data-testid="sequence-test-send"><Send className="w-3.5 h-3.5 mr-1" /> Test send</Button>
        </div>
      </div>

      {view?.progress && (
        <div className="text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="sequence-progress">
          <span>Active: <strong>{view.progress.active}</strong></span>
          <span>Completed: <strong>{view.progress.completed}</strong></span>
          <span>Stopped: <strong>{view.progress.stopped}</strong></span>
          <span>Not started: <strong>{view.progress.not_started}</strong></span>
          {view.stop_breakdown && Object.keys(view.stop_breakdown).length > 0 && (
            <span className="text-slate-400">({Object.entries(view.stop_breakdown).map(([k, v]) => `${v} ${STOP_LABEL[k] || k}`).join(", ")})</span>
          )}
        </div>
      )}
    </div>
  );
}
